import {
  Box,
  Checkbox,
  FormControl,
  MenuItem,
  Radio,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  dropDownSelectStyle,
  muiOutlinedInputStyle,
  radioButton,
} from '../../ChartAxes/CardComponents/ReusableStyles/CommonStyles';
import { TSlidingStandingAggregation } from '../../../types/types';
import ReplayIcon from '@mui/icons-material/Replay';
import { useState } from 'react';
interface StandingVsSlicingProps {
  onChange: (
    optionType: 'standing' | 'sliding' | 'standingsvssliding',
    idx: number,
    value: string | number,
    type: string,
    agg?: TSlidingStandingAggregation,
  ) => void;
  percentage: 'percentageFrom' | 'differenceFrom' | 'percentageDifferenceFrom';
  isFirst: boolean;
  isLast: boolean;
  isCurrent: boolean;
  isPNC: boolean;
  prev?: number;
  next?: number;
  aggregation?: TSlidingStandingAggregation;
}
function StandingVsSlicing({
  onChange: _onChange,
  percentage,
  isFirst,
  isLast,
  prev,
  next,
  aggregation,
}: StandingVsSlicingProps) {
  const percentages: Record<string, string>[] = [
    { key: 'percentageFrom', value: 'Percentage From' },
    { key: 'differenceFrom', value: 'Difference From' },
    { key: 'percentageDifferenceFrom', value: 'Percentage Difference From' },
  ];
  const standingSlidingAggregation: TSlidingStandingAggregation[] = ['sum', 'avg', 'min', 'max'];

  const dropdownSelections = {
    first: isFirst
      ? 'isFirst'
      : isLast
        ? 'isLast'
        : (prev ?? 0) < 0
          ? 'Previous'
          : prev === 0
            ? 'Current'
            : 'Next',
    second: next === undefined || next === 0 ? 'Current' : next < 0 ? 'Previous' : 'Next',
  };
  const [isCustomSelected, setIsCustomSelected] = useState({
    first: Math.abs(prev ?? 0) > 5 && Math.abs(prev ?? 0) !== 999,
    second: !!next && Math.abs(next) > 5 && Math.abs(next) !== 999,
  });
  const absPrev=Math.abs(prev??10);
    const absNext = next ? Math.abs(next) : 10;
    const [inputs, setInputs] = useState({
      first: absPrev !== 999 ? absPrev.toString() : '10',
      second: absNext && absNext !== 999 ? absNext.toString() : '10',
    });
  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const { name, value } = event.target;
    setInputs((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const needSecondRef = next !== undefined;
  const dropDownOptions = ['Previous', 'Current', 'Next'] as const;
  const values = [
    { label: 'All', value: '999' },
    { label: '1st', value: '1' },
    { label: '2nd', value: '2' },
    { label: '3rd', value: '3' },
    { label: '4th', value: '4' },
    { label: '5th', value: '5' },
    { label: 'Custom', value: 'Custom' },
  ];
  const handleChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;

    if (['isFirst', 'isLast'].includes(value)) {
      _onChange('standingsvssliding', 1, value === 'isFirst' ? 'first' : 'last', 'referenceWn');
      return;
    }
    if (value === 'Current') {
      _onChange(
        'standingsvssliding',
        name === 'first' ? 0 : 1,
        0,
        'changePrevNextDropDown',
        standingSlidingAggregation.includes(aggregation ?? 'sum') ? aggregation : 'sum',
      );
    } else {
      let modifiedInput = name === 'first' ? prev || 999 : next || 999;
      modifiedInput = value === 'Previous' ? -Math.abs(modifiedInput) : Math.abs(modifiedInput);
      _onChange(
        'standingsvssliding',
        name === 'first' ? 0 : 1,
        modifiedInput,
        'changePrevNextDropDown',
        standingSlidingAggregation.includes(aggregation ?? 'sum') ? aggregation : 'sum',
      );
    }
  };
  const handleNeedOFSecondRefChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    if (checked)
      setIsCustomSelected((prev) => ({
        ...prev,
        second: false,
      }));
    _onChange(
      'standingsvssliding',
      1,
      checked ? (prev === 0 ? 999 : 0) : -2,
      checked ? 'next' : 'trim',
    );
  };
  const handleInputChange = (event: React.FocusEvent<HTMLInputElement>) => {
    event.preventDefault();
    const { name} = event.target;
    const value = inputs[name as keyof typeof inputs] ?? 10;
    let modifiedInput = value === '' ? 1 : Number(value);
    if (modifiedInput > 100) modifiedInput = 100;
    if (modifiedInput < 1) modifiedInput = 1;
    modifiedInput =
      dropdownSelections[name as keyof typeof dropdownSelections] === 'Previous'
        ? -Math.abs(modifiedInput)
        : Math.abs(modifiedInput);
    _onChange(
      'sliding',
      name === 'first' ? 0 : 1,
      modifiedInput,
      name === 'first' ? 'prev' : 'next',
    );
    setInputs((prev) => ({
      ...prev,
      [name]: Math.abs(modifiedInput).toString(),
    }));
  };
  const handleDropdownChangeForRow = (event: SelectChangeEvent<string>) => {
    event.preventDefault();
    const { name, value } = event.target;
    if (value === 'Custom') {
      setIsCustomSelected((prev) => ({
        ...prev,
        [name]: true,
      }));
      const value =
        dropdownSelections[name as keyof typeof dropdownSelections] === 'Previous' ? -10 : 10;
      _onChange(
        'standingsvssliding',
        name === 'first' ? 0 : 1,
        value,
        name === 'first' ? 'prev' : 'next',
      );
    } else {
      let modifiedInput = Number(value);
      modifiedInput =
        dropdownSelections[name as keyof typeof dropdownSelections] === 'Previous'
          ? Math.abs(modifiedInput) * -1
          : modifiedInput;
      _onChange(
        'standingsvssliding',
        name === 'first' ? 0 : 1,
        modifiedInput,
        name === 'first' ? 'prev' : 'next',
      );
    }
  };
  const resetReferenceWindow = () => {
    _onChange('standingsvssliding', 0, -999, 'default');
    setIsCustomSelected({
      first: false,
      second: false,
    });
    setInputs({
      first:" 10",
      second: "10",
    });
  };
  return (
    <div onDoubleClick={(e) => e.stopPropagation()} style={{ width: '100%' }}>
      <div className="MenuSize" style={{ width: '100%' }}>
        <div>
          {percentages.map((option, index) => (
            <div className="commonOption2" key={index}>
              <Radio
                size="small"
                checked={percentage === option.key}
                onChange={(e) => {
                  _onChange('standingsvssliding', 0, option.key, 'percentage');
                }}
                value={option.key}
                sx={{
                  ...radioButton,
                  marginTop: '0',
                  paddingTop: '0',
                  paddingLeft: '15px',
                }}
              />
              <Typography
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '12px',
                  marginTop: '0',
                  padding: '0 15px 7px 0',
                }}
              >
                {option.value}
              </Typography>
            </div>
          ))}
        </div>

        <Typography
          sx={{
            display: 'flex',
            alignItems: 'center',
            marginBlock: '0.3rem',
          }}
        >
          <Typography
            sx={{
              fontSize: '12px',
              paddingLeft: '15px',
              fontWeight: '600',
              color: 'rgb(96, 96, 96)',
            }}
          >
            Reference Window
          </Typography>
          <Tooltip
            title="Reset reference window to default"
            placement="top"
            slotProps={{
              tooltip: {
                sx: {
                  fontSize: '12px',
                },
              },
            }}
          >
            <ReplayIcon
              sx={{ fontSize: '14px', marginLeft: '5px', cursor: 'pointer' }}
              onClick={resetReferenceWindow}
            />
          </Tooltip>
        </Typography>

        <div
          style={{
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              marginRight: '15px',
              marginLeft: '4px',
            }}
          >
            <Checkbox
              checked
              disabled
              size="small"
              sx={{ '& .MuiSvgIcon-root': { fontSize: 16 } }}
            />

            <Select
              labelId="prev-next-curr-1"
              id="first-dropdown"
              value={dropdownSelections.first}
              onChange={handleChange}
              name="first"
              sx={{ ...dropDownSelectStyle, minWidth: '100px', marginRight: '9px' }}
            >
              <MenuItem key="isFirst" value={'isFirst'}>
                First
              </MenuItem>
              {dropDownOptions.map((option) => (
                <MenuItem
                  key={option}
                  value={option}
                  disabled={
                    option === 'Current' && needSecondRef && dropdownSelections.second === 'Current'
                  }
                >
                  {option}
                </MenuItem>
              ))}

              <MenuItem key="isLast" value={'isLast'}>
                Last
              </MenuItem>
            </Select>
            {!['isFirst', 'isLast'].includes(dropdownSelections.first) &&
              dropdownSelections.first !== 'Current' &&
              !isCustomSelected.first && (
                <Select
                  labelId="prev-next-curr-1"
                  id="first-dropdown"
                  value={`${Math.abs(prev ?? 999)}`}
                  onChange={handleDropdownChangeForRow}
                  name="first"
                  sx={{ ...dropDownSelectStyle, minWidth: '60px' }}
                >
                  {values.map((option) => (
                    <MenuItem key={option.label} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              )}
            {!['isFirst', 'isLast'].includes(dropdownSelections.first) &&
              dropdownSelections.first !== 'Current' &&
              isCustomSelected.first && (
                <TextField
                  type="number"
                  value={Math.abs(Number(inputs.first))}
                  name="first"
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                    input: { style: { height: '25px' } },
                    htmlInput: { min: 0, max: 100 },
                  }}
                  sx={{
                    width: '95%',
                    '& .MuiInputBase-root': {
                      height: '2rem',
                      fontSize: '11px',
                    },
                    '& .MuiTextField-root': {
                      outline: 'none',
                      border: 'none',
                    },
                    border: 'none',
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: '#2bb9bb',
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      padding: '16.5px 14px', // Consistent padding for all browsers
                    },
                    // Firefox-specific styling
                    '@supports (-moz-appearance: none)': {
                      '& .MuiOutlinedInput-input': {
                        padding: '16.5px 2px 16.5px 14px',
                      },
                    },
                  }}
                  onBlur={handleInputChange}
                  onChange={onInputChange}
                />
              )}
          </Box>

          <Box
            sx={{
              display: !['isFirst', 'isLast'].includes(dropdownSelections.first) ? 'flex' : 'none',
              alignItems: 'center',
              marginRight: '15px',
              marginLeft: '4px',
            }}
          >
            <Checkbox
              checked={needSecondRef}
              onChange={handleNeedOFSecondRefChange}
              size="small"
              sx={{ '& .MuiSvgIcon-root': { fontSize: 16 } }}
            />

            <Select
              disabled={!needSecondRef}
              labelId="prev-next-curr-2"
              id="second-dropdown"
              value={dropdownSelections.second}
              onChange={handleChange}
              name="second"
              sx={{ ...dropDownSelectStyle, minWidth: '100px', marginRight: '9px' }}
            >
              {dropDownOptions.map((option) => (
                <MenuItem
                  key={option}
                  value={option}
                  disabled={option === 'Current' && dropdownSelections.first === 'Current'}
                >
                  {option}
                </MenuItem>
              ))}
            </Select>
            {dropdownSelections.second !== 'Current' && !isCustomSelected.second && (
              <Select
                disabled={!needSecondRef}
                labelId="prev-next-curr-2"
                id="second-dropdown"
                value={`${Math.abs(next ?? 999)}`}
                onChange={handleDropdownChangeForRow}
                name="second"
                sx={{ ...dropDownSelectStyle, minWidth: '60px' }}
              >
                {values.map((option) => (
                  <MenuItem key={option.label} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            )}
            {dropdownSelections.second !== 'Current' && isCustomSelected.second && (
              <TextField
                disabled={!needSecondRef}
                type="number"
                value={Math.abs(Number(inputs.second))}
                name="second"
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                  input: { style: { height: '25px' } },
                  htmlInput: { min: -1, max: 100 },
                }}
                sx={{
                  width: '95%',
                  '& .MuiInputBase-root': {
                    height: '2rem',
                    fontSize: '11px',
                  },
                  '& .MuiTextField-root': {
                    outline: 'none',
                    border: 'none',
                  },
                  border: 'none',
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: '#2bb9bb',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '16.5px 14px', // Consistent padding for all browsers
                  },
                  // Firefox-specific styling
                  '@supports (-moz-appearance: none)': {
                    '& .MuiOutlinedInput-input': {
                      padding: '16.5px 2px 16.5px 14px',
                    },
                  },
                }}
                onBlur={handleInputChange}
                onChange={onInputChange}
              />
            )}
          </Box>
        </div>

        <Typography
          sx={{
            fontSize: '12px',
            paddingLeft: '15px',
            fontWeight: '600',
            color: 'rgb(96, 96, 96)',
            marginTop: '12px',
          }}
        >
          Aggregation
        </Typography>
        <FormControl fullWidth>
          <Select
            disabled={isFirst || isLast}
            onChange={(e) => {
              e.stopPropagation();

              _onChange(
                'standingsvssliding',
                1,
                'pnc',
                'referenceWn',
                e.target.value as TSlidingStandingAggregation,
              );
            }}
            value={aggregation}
            sx={{ ...dropDownSelectStyle, margin: '3px 15px 7px 15px' }}
          >
            {standingSlidingAggregation.map((data, index) => (
              <MenuItem key={index} value={data} sx={{ fontSize: '12px' }}>
                {data.charAt(0).toUpperCase() + data.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
    </div>
  );
}

export default StandingVsSlicing;
